import { useCallback } from 'react';

import type { Rect } from '@aline/common';
import { isRenderModelLine, isRenderModelPage, Queries } from '@aline/common';
import { equals } from 'ramda';
import type { Editor } from 'slate';
import { Range } from 'slate';

import { useSlateSelector } from './editor-hooks';
import { useOpenHeaderOrFooterPageNum } from '../components';

/**
 * Get the rectangle containing a range, potentially across multiple pages. The returned topPx is
 * relative to the top of the scrollable window, and the returned leftPx is relative to the left of
 * each page. If the range is inside a header or footer, only the rect for the header or footer that
 * was opened is returned.
 *
 * Use the PageWidthWrapper component to account for the horizontal page position.
 */
export function useRangeRects(
  cMRange: Range | null
): { firstLineRect: Rect; lastLineRect: Rect } | null {
  const activeHeaderOrFooterPageNum = useOpenHeaderOrFooterPageNum();

  return useSlateSelector(
    useCallback(
      editor => {
        if (!cMRange) return null;

        return getRangeRects(editor, cMRange, {
          activeHeaderOrFooterPageNum,
        });
      },
      [activeHeaderOrFooterPageNum, cMRange]
    ),
    equals
  );
}

/**
 * Get the range rect for the first and last pages of the range and combine these into a single
 * rect.
 */
function getRangeRects(
  editor: Editor,
  cMRange: Range,
  options: {
    activeHeaderOrFooterPageNum: number | null;
  }
): { firstLineRect: Rect; lastLineRect: Rect } | null {
  const { activeHeaderOrFooterPageNum } = options;

  const rMRange = Queries.getRMLocationForActiveHeaderOrFooter(
    editor,
    Queries.getRMRangesFromCMRange(editor, cMRange),
    activeHeaderOrFooterPageNum
  );
  if (!rMRange) return null;

  const [rMStart, rMEnd] = Range.edges(rMRange);

  const rMStartLineEntry = Queries.renderModelAbove(editor, {
    at: rMStart,
    match: isRenderModelLine,
  });

  const rMEndLineEntry = Queries.renderModelAbove(editor, {
    at: rMEnd,
    match: isRenderModelLine,
  });

  if (!rMStartLineEntry || !rMEndLineEntry) return null;

  const startPageNum =
    activeHeaderOrFooterPageNum ??
    Queries.renderModelAbove(editor, { at: rMStart, match: isRenderModelPage })?.[0].pageNum;

  const endPageNum =
    activeHeaderOrFooterPageNum ??
    Queries.renderModelAbove(editor, { at: rMEnd, match: isRenderModelPage })?.[0].pageNum;

  if (startPageNum === undefined || endPageNum === undefined) return null;

  const startRect = Queries.getRangeLineIntersectionRectInDocument(
    editor,
    cMRange,
    rMStartLineEntry
  );
  const endRect = Queries.getRangeLineIntersectionRectInDocument(editor, cMRange, rMEndLineEntry);
  if (!startRect || !endRect) return null;

  return { firstLineRect: startRect, lastLineRect: endRect };
}
